import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { MutableRefObject } from 'react'
import { useIntl } from 'react-intl'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { nonNullable, unique } from 'lib/array/arrayUtils'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import { reconcileCartItems, isReconcileCartErrorResponse, type ReconcileCartResult } from 'tripPlanner/api/cartReconciliation'
import type { TripItineraryData } from 'tripPlanner/api/types'
import * as ChatKeys from 'tripPlanner/hooks/reactQueryKeys/chat'
import type { AnonymousClaimTransfer } from './useAnonymousChatClaiming'
import {
  extractFlightCartPanelOptions,
  hasLinkedFlightCartPanelItem,
  hasMissingLinkedFlightCartPanelItem,
  toFlightCartPanelProjectionSignature,
  type ExtractedFlightCartPanelOption,
} from '../utils/flightCartPanelUtils'
import {
  extractTripChatCartCandidates,
  getTripChatCartCandidateProjectionSignature,
  type TripChatCartCandidate,
} from '../utils/tripChatCartCandidateUtils'
import { getTripPlannerCandidateCartItem } from '../utils/tripPlannerCartItemsUtils'
import {
  tripPlannerCartItemsKey,
  useTripPlannerCartItems,
} from './useTripPlannerCartItems'

interface UseTripChatCartReconciliationArgs {
  chatId: number | undefined
  itineraryData: TripItineraryData | undefined
  isLoggedIn: boolean
  isStreaming: boolean
  isStreamOpen: boolean
  isManualItineraryDeletePending: boolean
  isFlightCartMutationPending: boolean
  waitForAnonymousChatClaim: (signal?: AbortSignal) => Promise<void>
  anonymousClaimTransfer: AnonymousClaimTransfer | undefined
}

interface StaleCartProjectionState {
  shouldReconcile: boolean
  signature: string | undefined
}

interface ProjectionAttempt {
  signature: string
  chatId: number
  multiCartId: string | undefined
  itineraryData: TripItineraryData | undefined
  cartItemsDataUpdatedAt: number
  cartItemsErrorUpdatedAt: number
  conflictRetryVersion: number
}

type ReconciliationTrigger = 'stale_projection' | 'anonymous_claim'

interface ReconciliationBlockers {
  isLoggedIn: boolean
  isStreaming: boolean
  isStreamOpen: boolean
  isManualItineraryDeletePending: boolean
  isFlightCartMutationPending: boolean
}

interface ReconciliationMutationVariables {
  signature: string
  projectionSignature: string
  chatId: number
  multiCartId: string | undefined
  attempt: ProjectionAttempt
  trigger: ReconciliationTrigger
}

interface StartReconciliationArgs {
  trigger: ReconciliationTrigger
  signature: string
  projectionSignature: string
  successfulSignatureRef: MutableRefObject<string | undefined>
}

interface SuccessfulReconciliationProjection {
  chatId: number
  projectionSignature: string
}

class ReconciliationSkippedError extends Error {}

const REPAIRABLE_CART_STATUSES: ReadonlySet<TripChatCartCandidate['cartStatus']> = new Set([
  'active',
  'not_synced',
])

const EMPTY_CART_ITEMS_MAP = new Map<string, App.MultiCartItem>()

function isRepairableCandidate(item: TripChatCartCandidate): boolean {
  return REPAIRABLE_CART_STATUSES.has(item.cartStatus)
}

function hasMissingLinkedCandidateCartItem(
  candidates: Array<TripChatCartCandidate>,
  cartItemsMap: Map<string, App.MultiCartItem>,
): boolean {
  return candidates.some(item => (
    isRepairableCandidate(item) &&
    !!item.cartItemId &&
    !getTripPlannerCandidateCartItem(cartItemsMap, item)
  ))
}

function isSameProjectionAttempt(
  attempt: ProjectionAttempt | undefined,
  next: ProjectionAttempt,
): boolean {
  return (
    !!attempt &&
    attempt.signature === next.signature &&
    attempt.chatId === next.chatId &&
    attempt.multiCartId === next.multiCartId &&
    attempt.itineraryData === next.itineraryData &&
    attempt.cartItemsDataUpdatedAt === next.cartItemsDataUpdatedAt &&
    attempt.cartItemsErrorUpdatedAt === next.cartItemsErrorUpdatedAt &&
    attempt.conflictRetryVersion === next.conflictRetryVersion
  )
}

function isReconciliationBlocked(blockers: ReconciliationBlockers): boolean {
  return (
    !blockers.isLoggedIn ||
    blockers.isStreaming ||
    blockers.isStreamOpen ||
    blockers.isManualItineraryDeletePending ||
    blockers.isFlightCartMutationPending
  )
}

function getStaleCartProjectionState({
  multiCartId,
  candidates,
  flightItems,
  cartItemsMap,
  isCartItemsLoading,
  isCartItemsFetching,
  isCartItemsSuccess,
  isMissingCart,
  hasLinkedFlightCartItems,
}: {
  multiCartId: string | undefined
  candidates: Array<TripChatCartCandidate>
  flightItems: Array<ExtractedFlightCartPanelOption>
  cartItemsMap: Map<string, App.MultiCartItem>
  isCartItemsLoading: boolean
  isCartItemsFetching: boolean
  isCartItemsSuccess: boolean
  isMissingCart: boolean
  hasLinkedFlightCartItems: boolean
}): StaleCartProjectionState {
  const repairableCandidates = candidates.filter(isRepairableCandidate)
  const shouldWaitForCartItems = !!multiCartId && (isCartItemsLoading || isCartItemsFetching)
  if (shouldWaitForCartItems) {
    return { shouldReconcile: false, signature: undefined }
  }

  const hasCartableProjectionRows = repairableCandidates.length > 0 || hasLinkedFlightCartItems
  const canEvaluateLinkedCartItems = !!multiCartId && isCartItemsSuccess
  const shouldReconcile = (
    (!multiCartId && repairableCandidates.length > 0) ||
    (!!multiCartId && isMissingCart && hasCartableProjectionRows) ||
    (canEvaluateLinkedCartItems && hasMissingLinkedCandidateCartItem(candidates, cartItemsMap)) ||
    candidates.some(item => item.cartStatus === 'not_synced') ||
    (canEvaluateLinkedCartItems && hasMissingLinkedFlightCartPanelItem(flightItems))
  )

  if (!shouldReconcile) {
    return { shouldReconcile: false, signature: undefined }
  }

  return {
    shouldReconcile: true,
    signature: getTripChatCartCandidateProjectionSignature(
      multiCartId,
      candidates,
      nonNullable(flightItems.map(toFlightCartPanelProjectionSignature)),
    ),
  }
}

function getCartIdsToInvalidate(
  currentMultiCartId: string | undefined,
  result: ReconcileCartResult | undefined,
): Array<string> {
  return unique(nonNullable([
    currentMultiCartId,
    result?.previousMultiCartId,
    result?.multiCartId,
  ]))
}

function hasClaimReconciliationProjectionRows(
  candidates: Array<TripChatCartCandidate>,
  hasLinkedFlightCartItems: boolean,
): boolean {
  return candidates.length > 0 || hasLinkedFlightCartItems
}

function isSuccessfulReconciliationProjection(
  successfulProjection: SuccessfulReconciliationProjection | undefined,
  chatId: number,
  projectionSignature: string,
): boolean {
  return (
    !!successfulProjection &&
    successfulProjection.chatId === chatId &&
    successfulProjection.projectionSignature === projectionSignature
  )
}

const CLAIM_COMPLETION_WAIT_TIMEOUT_MS = 30000

async function waitForClaimCompletionBeforeReconciliation(
  waitForAnonymousChatClaim: (signal?: AbortSignal) => Promise<void>,
): Promise<void> {
  const abortController = new AbortController()
  let didTimeout = false
  let timeoutId: ReturnType<typeof setTimeout> | undefined
  const timeoutPromise = new Promise<void>((resolve) => {
    timeoutId = setTimeout(() => {
      didTimeout = true
      abortController.abort()
      resolve()
    }, CLAIM_COMPLETION_WAIT_TIMEOUT_MS)
  })

  try {
    await Promise.race([
      waitForAnonymousChatClaim(abortController.signal),
      timeoutPromise,
    ])
  } finally {
    if (timeoutId) {
      clearTimeout(timeoutId)
    }
  }
  if (didTimeout) {
    throw new ReconciliationSkippedError()
  }
}

export function useTripChatCartReconciliation({
  chatId,
  itineraryData,
  isLoggedIn,
  isStreaming,
  isStreamOpen,
  isManualItineraryDeletePending,
  isFlightCartMutationPending,
  waitForAnonymousChatClaim,
  anonymousClaimTransfer,
}: UseTripChatCartReconciliationArgs): void {
  const intl = useIntl()
  const queryClient = useQueryClient()
  const multiCartId = itineraryData?.multiCartId
  const [conflictRetryVersion, setConflictRetryVersion] = useState(0)
  const inFlightSignatureRef = useRef<string | undefined>(undefined)
  const attemptedProjectionRef = useRef<ProjectionAttempt | undefined>(undefined)
  const successfulStaleSignatureRef = useRef<string | undefined>(undefined)
  const successfulClaimSignatureRef = useRef<string | undefined>(undefined)
  const successfulProjectionRef = useRef<SuccessfulReconciliationProjection | undefined>(undefined)
  const conflictRetrySignatureRef = useRef<string | undefined>(undefined)
  const warnedSignatureRef = useRef<string | undefined>(undefined)
  const latestChatIdRef = useRef(chatId)
  const latestMultiCartIdRef = useRef(multiCartId)
  const latestBlockersRef = useRef<ReconciliationBlockers>({
    isLoggedIn,
    isStreaming,
    isStreamOpen,
    isManualItineraryDeletePending,
    isFlightCartMutationPending,
  })
  latestBlockersRef.current = {
    isLoggedIn,
    isStreaming,
    isStreamOpen,
    isManualItineraryDeletePending,
    isFlightCartMutationPending,
  }
  latestChatIdRef.current = chatId
  latestMultiCartIdRef.current = multiCartId

  const candidates = useMemo(
    () => extractTripChatCartCandidates(itineraryData),
    [itineraryData],
  )

  const flightItemsWithoutCartSnapshot = useMemo(() => {
    if (!itineraryData?.stops) return []
    return itineraryData.stops.flatMap(stop => extractFlightCartPanelOptions(stop.transports, EMPTY_CART_ITEMS_MAP))
  }, [itineraryData])
  const hasLinkedFlightCartItems = useMemo(
    () => hasLinkedFlightCartPanelItem(flightItemsWithoutCartSnapshot),
    [flightItemsWithoutCartSnapshot],
  )

  const {
    cartItemsMap,
    isLoading: isCartItemsLoading,
    isFetching: isCartItemsFetching,
    isSuccess: isCartItemsSuccess,
    isMissingCart,
    dataUpdatedAt: cartItemsDataUpdatedAt,
    errorUpdatedAt: cartItemsErrorUpdatedAt,
  } = useTripPlannerCartItems({
    multiCartId,
    isLoggedIn,
    enabled: candidates.length > 0 || hasLinkedFlightCartItems,
  })

  const flightItems = useMemo(() => {
    if (!itineraryData?.stops) return []
    return itineraryData.stops.flatMap(stop => extractFlightCartPanelOptions(stop.transports, cartItemsMap))
  }, [itineraryData, cartItemsMap])

  const staleState = useMemo(
    () => getStaleCartProjectionState({
      multiCartId,
      candidates,
      flightItems,
      cartItemsMap,
      isCartItemsLoading,
      isCartItemsFetching,
      isCartItemsSuccess,
      isMissingCart,
      hasLinkedFlightCartItems,
    }),
    [
      multiCartId,
      candidates,
      flightItems,
      cartItemsMap,
      isCartItemsLoading,
      isCartItemsFetching,
      isCartItemsSuccess,
      isMissingCart,
      hasLinkedFlightCartItems,
    ],
  )

  const scopedStaleSignature = useMemo(() => {
    if (!chatId || !staleState.signature) return undefined
    return JSON.stringify({
      chatId,
      projection: staleState.signature,
    })
  }, [chatId, staleState.signature])

  const claimProjectionSignature = useMemo(() => {
    if (!hasClaimReconciliationProjectionRows(candidates, hasLinkedFlightCartItems)) return undefined

    return getTripChatCartCandidateProjectionSignature(
      multiCartId,
      candidates,
      nonNullable(flightItems.map(toFlightCartPanelProjectionSignature)),
    )
  }, [
    candidates,
    hasLinkedFlightCartItems,
    multiCartId,
    flightItems,
  ])

  const scopedClaimSignature = useMemo(() => {
    if (!chatId || !anonymousClaimTransfer) return undefined
    if (anonymousClaimTransfer.chatId !== chatId) return undefined
    if (!itineraryData) return undefined
    if (!claimProjectionSignature) return undefined

    return JSON.stringify({
      chatId,
      anonymousClaimTransferVersion: anonymousClaimTransfer.version,
      projection: claimProjectionSignature,
    })
  }, [
    chatId,
    anonymousClaimTransfer,
    itineraryData,
    claimProjectionSignature,
  ])

  const invalidateReconciliationQueries = useCallback(
    async(chatIdToInvalidate: number, multiCartIdToInvalidate: string | undefined, result: ReconcileCartResult | undefined) => {
      const cartIds = getCartIdsToInvalidate(multiCartIdToInvalidate, result)
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ChatKeys.detail(chatIdToInvalidate) }),
        queryClient.invalidateQueries({ queryKey: ChatKeys.itineraryDetail(chatIdToInvalidate) }),
        ...cartIds.map(cartId => queryClient.invalidateQueries({ queryKey: tripPlannerCartItemsKey(cartId) })),
      ])
    },
    [queryClient],
  )

  const { mutate, isLoading: isReconcileLoading } = useMutation({
    mutationFn: async(variables: ReconciliationMutationVariables) => {
      await waitForClaimCompletionBeforeReconciliation(waitForAnonymousChatClaim)
      if (
        isReconciliationBlocked(latestBlockersRef.current) ||
        latestChatIdRef.current !== variables.chatId ||
        latestMultiCartIdRef.current !== variables.multiCartId
      ) {
        throw new ReconciliationSkippedError()
      }
      attemptedProjectionRef.current = variables.attempt
      return reconcileCartItems(variables.chatId)
    },
    retry: (failureCount, error) => isReconcileCartErrorResponse(error) && error.status === 502 && failureCount < 1,
    onSuccess: async(result, variables) => {
      await invalidateReconciliationQueries(variables.chatId, variables.multiCartId, result)
      successfulProjectionRef.current = {
        chatId: variables.chatId,
        projectionSignature: variables.projectionSignature,
      }
      if (variables.trigger === 'anonymous_claim') {
        successfulClaimSignatureRef.current = variables.signature
      } else {
        successfulStaleSignatureRef.current = variables.signature
        if (scopedClaimSignature) {
          successfulClaimSignatureRef.current = scopedClaimSignature
        }
      }
    },
    onError: async(error, variables) => {
      if (error instanceof ReconciliationSkippedError) return
      if (isReconcileCartErrorResponse(error) && error.status === 409) {
        await invalidateReconciliationQueries(variables.chatId, variables.multiCartId, undefined)
        if (conflictRetrySignatureRef.current !== variables.signature) {
          conflictRetrySignatureRef.current = variables.signature
          attemptedProjectionRef.current = undefined
          setConflictRetryVersion(version => version + 1)
          return
        }
      }
      if (warnedSignatureRef.current === variables.signature) return
      warnedSignatureRef.current = variables.signature
      showSnackbar(intl.formatMessage({
        id: 'QBm4GWnkbw8H7HAYrhdq',
        defaultMessage: "We couldn't prepare checkout yet. Your trip items are still visible. Try again in a moment.",
        description: 'Warning snackbar when TripChat cart reconciliation fails without blocking itinerary cart visibility',
      }), 'warning')
    },
    onSettled: (_data, _error, variables) => {
      if (inFlightSignatureRef.current === variables?.signature) {
        inFlightSignatureRef.current = undefined
      }
    },
  })

  useEffect(() => {
    if (successfulStaleSignatureRef.current && successfulStaleSignatureRef.current !== scopedStaleSignature) {
      successfulStaleSignatureRef.current = undefined
    }
    if (conflictRetrySignatureRef.current && conflictRetrySignatureRef.current !== scopedStaleSignature) {
      conflictRetrySignatureRef.current = undefined
    }
  }, [scopedStaleSignature])

  useEffect(() => {
    if (successfulClaimSignatureRef.current && successfulClaimSignatureRef.current !== scopedClaimSignature) {
      successfulClaimSignatureRef.current = undefined
    }
  }, [scopedClaimSignature])

  const startReconciliation = useCallback(({
    trigger,
    signature,
    projectionSignature,
    successfulSignatureRef,
  }: StartReconciliationArgs) => {
    if (!isLoggedIn) return
    if (!chatId) return
    if (isStreaming || isStreamOpen || isManualItineraryDeletePending || isFlightCartMutationPending) return
    if (inFlightSignatureRef.current) return
    if (successfulSignatureRef.current === signature) return
    if (isSuccessfulReconciliationProjection(successfulProjectionRef.current, chatId, projectionSignature)) return
    if (isReconcileLoading) return

    const attempt: ProjectionAttempt = {
      signature,
      chatId,
      multiCartId,
      itineraryData,
      cartItemsDataUpdatedAt,
      cartItemsErrorUpdatedAt,
      conflictRetryVersion,
    }
    if (isSameProjectionAttempt(attemptedProjectionRef.current, attempt)) return

    inFlightSignatureRef.current = signature
    mutate({
      signature,
      projectionSignature,
      chatId,
      multiCartId,
      attempt,
      trigger,
    })
  }, [
    chatId,
    multiCartId,
    itineraryData,
    isLoggedIn,
    isStreaming,
    isStreamOpen,
    isManualItineraryDeletePending,
    isFlightCartMutationPending,
    cartItemsDataUpdatedAt,
    cartItemsErrorUpdatedAt,
    conflictRetryVersion,
    isReconcileLoading,
    mutate,
  ])

  useEffect(() => {
    if (!staleState.shouldReconcile || !scopedStaleSignature || !staleState.signature) return
    startReconciliation({
      trigger: 'stale_projection',
      signature: scopedStaleSignature,
      projectionSignature: staleState.signature,
      successfulSignatureRef: successfulStaleSignatureRef,
    })
  }, [
    staleState.shouldReconcile,
    staleState.signature,
    scopedStaleSignature,
    startReconciliation,
  ])

  useEffect(() => {
    if (!scopedClaimSignature || !claimProjectionSignature) return
    startReconciliation({
      trigger: 'anonymous_claim',
      signature: scopedClaimSignature,
      projectionSignature: claimProjectionSignature,
      successfulSignatureRef: successfulClaimSignatureRef,
    })
  }, [
    scopedClaimSignature,
    claimProjectionSignature,
    startReconciliation,
  ])
}
